import { AppError } from "../errors/index.js";
/**
 * حساب مواعيد التكرار — **والمنطقة تُقرأ**.
 *
 * هذا الملف يُغلق أربعة أعطاب في v2، كلُّها في ميزةٍ واحدة:
 *
 *  1. **المنطقة كانت حقلاً مطلوباً محقَّقاً محفوظاً ومُعاداً في كل استجابة — ولا
 *     يُقرأ في الحساب مرّةً واحدة.** فقاعدةٌ موسومة بالرياض تتصرّف مثل موسومةٍ
 *     بنيويورك، والحسابُ كلّه بتوقيت السيرفر المحلّي.
 *  2. **والشهريُّ المربوط بالحادي والثلاثين ينزلق إلى الأول ويبقى هناك للأبد** —
 *     لأن `setMonth(+1)` على ٣١ يناير يفيض إلى ٣ مارس، ثم يُحسب الشهر التالي من
 *     الرقم المنزلق لا من الأصل.
 *  3. **و«مخصَّص» كانت قيمةً مقبولةً تعني «يومياً» بصمت**، وحقلُ إعداداتها يُكتب
 *     ولا يُقرأ في المستودع كلّه. فما لا يُعرَّف لا يُولَد هنا.
 *  4. **والتوقيت الصيفي كان يزحزح ساعة التوليد** في منطقة السيرفر.
 *
 * والعلاج في الثلاثة الأولى واحد: **اليومُ المطلوب محفوظٌ مستقلاً عن الموعد
 * المحسوب**، والحسابُ يبني تاريخاً جديداً من مكوّناته في منطقة القاعدة — لا
 * يزيد على التاريخ السابق.
 */
export const RECURRENCE_FREQUENCIES = ["daily", "weekly", "monthly"];
export const RECURRENCE_LABEL = {
    daily: "cards.recurrence.daily",
    weekly: "cards.recurrence.weekly",
    monthly: "cards.recurrence.monthly",
};
export const MAX_RECURRENCE_INTERVAL = 52;
/**
 * يرفض القاعدة غير المكتملة — **ويسمّي الحقل**.
 *
 * والمنطقة تُتحقَّق أنها منطقةٌ حقيقية، لا نصٌّ يُقبَل ويُحفَظ ويُعرَض وكأنه يعمل.
 */
export function assertRecurrenceRule(rule) {
    if (!RECURRENCE_FREQUENCIES.includes(rule.frequency)) {
        throw new AppError("VALIDATION_FAILED", {
            fields: [{ field: "frequency", messageKey: "cards.recurrence.none" }],
        });
    }
    if (!Number.isInteger(rule.interval) ||
        rule.interval < 1 ||
        rule.interval > MAX_RECURRENCE_INTERVAL) {
        throw new AppError("VALIDATION_FAILED", {
            fields: [{ field: "interval", messageKey: "cards.recurrence.none" }],
        });
    }
    assertTimezone(rule.timezone);
    if (rule.frequency === "monthly") {
        const day = rule.monthDay;
        if (day === undefined || !Number.isInteger(day) || day < 1 || day > 31) {
            throw new AppError("VALIDATION_FAILED", {
                fields: [{ field: "monthDay", messageKey: "cards.recurrence.monthly" }],
            });
        }
    }
    if (rule.frequency === "weekly") {
        const day = rule.weekDay;
        if (day === undefined || !Number.isInteger(day) || day < 0 || day > 6) {
            throw new AppError("VALIDATION_FAILED", {
                fields: [{ field: "weekDay", messageKey: "cards.recurrence.weekly" }],
            });
        }
    }
}
/** منطقةٌ حقيقية أو رفضٌ بكوده — لا نصٌّ يُحفَظ ويُعرَض ولا يعمل */
export function assertTimezone(timezone) {
    try {
        new Intl.DateTimeFormat("en-US", { timeZone: timezone }).format(new Date());
    }
    catch {
        throw new AppError("VALIDATION_FAILED", {
            fields: [{ field: "timezone", messageKey: "clients.timezoneRequired" }],
        });
    }
}
function partsIn(at, timezone) {
    const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
    const read = (type) => {
        const part = formatter.formatToParts(at).find((p) => p.type === type);
        return part ? Number(part.value) : 0;
    };
    return {
        year: read("year"),
        month: read("month"),
        day: read("day"),
        // منتصف الليل يُقرأ `24` في بعض البيئات — ويُعنى به صفر
        hour: read("hour") % 24,
        minute: read("minute"),
    };
}
/** ما يقابل هذه الأجزاء من لحظةٍ عالمية، في تلك المنطقة */
function instantOf(parts, timezone) {
    /**
     * يُبنى تخميناً ثم يُصحَّح بالفارق المقيس — **فالتوقيت الصيفي محسوبٌ لا مفترَض**.
     *
     * وفي v2 كان الحساب بـ`Date` المحلّية، فانتقالُ الصيف **يزحزح ساعة التوليد**.
     */
    const guess = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
    const shown = partsIn(new Date(guess), timezone);
    const shownAsUtc = Date.UTC(shown.year, shown.month - 1, shown.day, shown.hour, shown.minute);
    return new Date(guess + (guess - shownAsUtc));
}
const daysInMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();
/**
 * الموعد التالي بعد `from` — **محسوبٌ من القاعدة، لا من الموعد السابق**.
 *
 * وهذا هو الفرق الذي يمنع الانزلاق: الشهريُّ المربوط بـ٣١ يُحسب **من اليوم
 * المطلوب** في كل شهر، فيُقصّ إلى آخر يومٍ في فبراير **ويرجع إلى ٣١ في مارس**.
 * وفي v2 كان يُحسب من التاريخ المنزلق، فينزلق إلى الأول **ويبقى هناك للأبد**.
 */
export function nextOccurrence(rule, anchor, after) {
    assertRecurrenceRule(rule);
    const base = partsIn(anchor, rule.timezone);
    switch (rule.frequency) {
        case "daily": {
            let at = instantOf(base, rule.timezone);
            const step = rule.interval * 24 * 60 * 60 * 1000;
            /** يُقدَّم حتى يتجاوز `after` — فانقطاعٌ طويل لا يولّد دفعةً بأثرٍ رجعيّ */
            const jumps = Math.max(0, Math.ceil((after.getTime() - at.getTime() + 1) / step));
            at = new Date(at.getTime() + jumps * step);
            /** ويُعاد تثبيت الساعة في المنطقة، فالانتقال الصيفي لا يزحزحها */
            const moved = partsIn(at, rule.timezone);
            return instantOf({ ...moved, hour: base.hour, minute: base.minute }, rule.timezone);
        }
        case "weekly": {
            const want = rule.weekDay ?? 0;
            let cursor = { ...base };
            /** أوّل يومٍ يطابق يومَ الأسبوع المطلوب، من المرساة */
            for (let guard = 0; guard < 7; guard += 1) {
                const at = instantOf(cursor, rule.timezone);
                if (at.getUTCDay() === want || weekDayIn(at, rule.timezone) === want)
                    break;
                cursor = advanceDays(cursor, 1);
            }
            let at = instantOf(cursor, rule.timezone);
            const step = rule.interval * 7 * 24 * 60 * 60 * 1000;
            const jumps = Math.max(0, Math.ceil((after.getTime() - at.getTime() + 1) / step));
            at = new Date(at.getTime() + jumps * step);
            const moved = partsIn(at, rule.timezone);
            return instantOf({ ...moved, hour: base.hour, minute: base.minute }, rule.timezone);
        }
        case "monthly": {
            const want = rule.monthDay ?? base.day;
            let year = base.year;
            let month = base.month;
            for (let guard = 0; guard < 400; guard += 1) {
                /** **القصّ لا الانزلاق**: فبرايرُ يأخذ آخر يومه، ومارسُ يرجع إلى ٣١ */
                const day = Math.min(want, daysInMonth(year, month));
                const at = instantOf({ ...base, year, month, day }, rule.timezone);
                if (at.getTime() > after.getTime())
                    return at;
                month += rule.interval;
                while (month > 12) {
                    month -= 12;
                    year += 1;
                }
            }
            throw new AppError("INTERNAL", {
                context: { reason: "recurrence-next-not-found", frequency: rule.frequency },
            });
        }
    }
}
function weekDayIn(at, timezone) {
    const name = new Intl.DateTimeFormat("en-US", { timeZone: timezone, weekday: "short" }).format(at);
    return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(name);
}
function advanceDays(parts, days) {
    const at = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
    return {
        ...parts,
        year: at.getUTCFullYear(),
        month: at.getUTCMonth() + 1,
        day: at.getUTCDate(),
    };
}
//# sourceMappingURL=recurrence.js.map